import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import reportService from '../../../../services/report.service';
import { useStores } from '../../../store';
import { BlockedSession, TotalSession } from '../../../../dtos/reports-data-response';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import { Chart, ChartData, ChartOptions, LegendElement, LegendItem, registerables, ScriptableContext, TimeUnit } from 'chart.js';
Chart.register(...registerables);
import './network-activity.scss';
import Loader from '../../../shared/loader/loader';
import { getTimeUnit } from '../../../../utils/datesUtils';
import moment from 'moment/moment';
import { TooltipLinePlugin } from '../../../../utils/chart';
import { TimeRangeProps } from '../../../../dtos/reports-data-request';
import { NoResult } from '../../../shared/no-result/no-result';
import { TryAgainMessage } from 'src/components/shared/try-again-message/try-again-message';
import { LevelName } from '@src/utils/enums';

type Session = TotalSession | BlockedSession;

type LabelMapValueType = {
    label: string;
    color: string;
};

type SessionKey = Exclude<keyof TotalSession | keyof BlockedSession, 'time'>;

const LABEL_MAP: Record<SessionKey, LabelMapValueType> = {
    allowedSessions: { label: 'Allowed', color: '45, 126, 36' },
    blockedSessions: { label: 'Blocked', color: '199, 0, 50' },
    geoFilteringSessions: { label: 'Geo Filtering', color: '0, 87, 184' },
    webFilteringSessions: { label: 'Web Filtering', color: '89, 199, 250' },
    appSessions: { label: 'Apps', color: '255, 176, 0' },
    serviceSessions: { label: 'Services', color: '234, 113, 47' },
    dynamicDefenceSessions: { label: 'DD Shield', color: '0, 159, 219' },
    advancedThreatSessions: { label: 'Premium Threat', color: '175, 41, 187' }
};

function getSessionLabel(key: SessionKey, serviceLevel: string, circuitType: string): string {
    if (key === 'advancedThreatSessions' && serviceLevel === LevelName.SILVER) {
        return 'DNS Protection';
    }
    return LABEL_MAP[key].label;
}

function getSessionValue(item: Session, key: SessionKey): number {

    const value = item[key as keyof typeof item];
    return typeof value === 'number' ? value : 0;
}

const whiteColorForUnselect = `rgb(255,255,255)`;

enum ACTIVITY_TYPE {
    TOTAL = 'total',
    BLOCKED = 'blocked'
}

export const NetworkActivity: React.FC<TimeRangeProps> = observer(({ from, to }) => {
    const [chartData, setChartData] = useState<ChartData<'line'> | null>(null);
    const [options, setOptions] = useState<ChartOptions<'line'>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [type, setType] = useState<ACTIVITY_TYPE>(ACTIVITY_TYPE.TOTAL);
    const [hasError, setHasError] = useState<boolean>(false);
    const { customerStore } = useStores();

    useEffect(() => {
        setIsLoading(true);
        setHasError(false);
        reportService.getNetworkActivity(customerStore.customerId, customerStore.serviceId, type, from, to)
            .then(res => {
                if (res.length) {
                    const sessionKeys = Object.keys(res[0]).filter(key => key !== 'time');
                    convertDataToChart(res, sessionKeys as Array<SessionKey>);
                } else {
                    setChartData(null);
                }
            })
            .catch(() => setHasError(true))
            .finally(() => setIsLoading(false));
    }, [from, to, type, customerStore.customerId, customerStore.serviceId]);

    const findColorByDataset = (item: any, sessionKeys: any) => {
        const datasetIndex = item.datasetIndex;
        const sessionColors: string[] = sessionKeys.map((key: keyof typeof LABEL_MAP) => (LABEL_MAP[key] as LabelMapValueType).color);
        return { color: sessionColors[datasetIndex], datasetIndex };
    };

    const convertDataToChart = (data: Session[], sessionKeys: Array<SessionKey>) => {
        const unit = getTimeUnit(data);
        const maxVal = Math.max(...data.map(item => Math.max(...sessionKeys.map(key => getSessionValue(item, key)))));
        const paddedMaxVal = maxVal * 1.05;
        // Get serviceLevel and circuitType from customerStore
        const serviceLevel = customerStore.selectedLocation?.serviceLevel || '';
        const circuitType = customerStore.selectedLocation?.circuitType || '';
        setChartData({
            labels: data.map(item => item.time),
            datasets: sessionKeys.map((key: SessionKey) => ({
                line: null,
                label: getSessionLabel(key, serviceLevel, circuitType),
                data: data.map((item) => getSessionValue(item, key)),
                fill: false,
                backgroundColor: `rgb(${(LABEL_MAP[key] as LabelMapValueType).color})`,
                tension: 0.2,
                pointRadius: 0.01,
                pointHoverRadius: 3,
                borderWidth: 1,
                borderColor: `rgb(${(LABEL_MAP[key] as LabelMapValueType).color})`,
                hoverBorderWidth: 2,
                pointBackgroundColor: 'white',
            })),
        });
        setOptions({
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false,
                axis: 'x',
                includeInvisible: false,
            },
            scales: {
                x: {
                    type: 'time',
                    time: {
                        unit: unit as TimeUnit,
                        displayFormats: {
                            hour: 'HH:mm',
                            day: 'M/D',
                            month: 'MMM YYYY'
                        }
                    },
                    display: true,
                    title: {
                        display: false,
                    },
                    grid: {
                        drawOnChartArea: false,
                    },
                },
                y: {
                    grid: {
                        offset: true,
                    },
                    beginAtZero: true,
                    suggestedMax: paddedMaxVal
                },
            },
            plugins: {
                tooltip: {
                    mode: 'index',
                    intersect: false,
                    enabled: true,
                    position: 'nearest',
                    caretPadding: 8,
                    backgroundColor: 'rgba(0, 0, 0, 1)',
                    titleFont: {
                        size: 10,
                        font: 'ATTAleckSansBold'
                    } as any,
                    bodyFont: {
                        size: 11,
                        font: 'ATTAleckSansBold'
                    } as any,
                    boxWidth: 8,
                    boxHeight: 8,
                    padding: 12,
                    titleMarginBottom: 10,
                    bodySpacing: 8,
                    boxPadding: 5,
                    callbacks: {
                        title: (tooltipItems: any): string => {
                            const timeValue = tooltipItems[0]?.label;
                            if (timeValue) {
                                return moment(timeValue as string).format('MMMM D, YYYY HH:mm');
                            }
                            return '';
                        },
                        label: (context: any): string => {
                            return `${context.dataset.label}  ${Number(context.parsed.y).toLocaleString()}`;
                        },
                        labelColor: (context: any) => {
                            return {
                                borderColor: '#fff',
                                backgroundColor: context.dataset.backgroundColor,
                                borderWidth: 0.5,
                                borderRadius: 4,
                            };
                        },
                    },
                },
                legend: {
                    position: 'top',
                    align: 'end',
                    display: true,
                    labels: {
                        boxWidth: 16,
                        boxHeight: 16,
                        useBorderRadius: true,
                        borderRadius: 3,
                    },
                    title: {
                        color: '#454B52'
                    },
                    onHover: (_evt: any, item: LegendItem, legend: LegendElement<'line'>) => {
                        const { color, datasetIndex } = findColorByDataset(item, sessionKeys);
                        const chart = legend.chart;
                        const dataset = chart.data.datasets[datasetIndex];
                        dataset.fill = 'start';
                        dataset.backgroundColor = (context: ScriptableContext<'line'>) => {
                            const ctx = context.chart.ctx;
                            const gradientHeight = context.chart.height;
                            const gradient = ctx.createLinearGradient(0, 0, 0, gradientHeight);
                            gradient.addColorStop(0, `rgba(${color}, 0.20)`);
                            gradient.addColorStop(1, `rgba(${color}, 0.00)`);
                            return gradient;
                        };
                        chart.update();
                    },
                    onLeave: (_evt: any, item: LegendItem, legend: LegendElement<'line'>) => {
                        const { color, datasetIndex } = findColorByDataset(item, sessionKeys);
                        const chart = legend.chart;
                        const dataset = chart.data.datasets[datasetIndex];
                        dataset.fill = false;
                        dataset.backgroundColor = chart.isDatasetVisible(datasetIndex) ? `rgb(${color})` : whiteColorForUnselect;
                        chart.update();
                    },
                }
            }
        });
    };

    const renderChartContent = () => {
        if (isLoading) {
            return <Loader type="component" />;
        }
        if (hasError) {
            return <TryAgainMessage />;
        }
        return !chartData ? <NoResult title="No data to display" subTitle="Change timeframe and try again" /> :
            <>
                <div className="chart-title">SESSIONS</div>
                <Line data={chartData} plugins={[TooltipLinePlugin]} options={options} />
            </>;
    };

    return (
        <div className="network-activity">
            <div className="network-activity-header">
                <div className="network-activity-title">Network Activity</div>
                <div className="network-activity-tabs">
                    {Object.values(ACTIVITY_TYPE).map(tab => (
                        <button type="button" key={tab}
                            onClick={() => setType(tab)}
                            className={`tab ${type === tab ? 'active' : ''}`}>
                            {tab}
                        </button>
                    ))}
                </div>
            </div>
            <div className="network-activity-chart">
                {renderChartContent()}
            </div>
        </div>
    );
});